import React, { useState, useEffect } from 'react';
import {
  Paper,
  Box,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  InputAdornment,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  PersonRemove as PersonRemoveIcon,
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import axios from 'axios';

interface Contact {
  id: string;
  phoneNumber: string;
  name: string;
  email?: string;
  note?: string;
}

interface GroupDetailProps {
  groupId: string;
  groupName: string;
  onBack: () => void;
}

const GroupDetail: React.FC<GroupDetailProps> = ({ groupId, groupName, onBack }) => {
  const [members, setMembers] = useState<Contact[]>([]);
  const [filteredMembers, setFilteredMembers] = useState<Contact[]>([]);
  const [allContacts, setAllContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState<string[]>([]);
  const [contactSearchQuery, setContactSearchQuery] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  useEffect(() => {
    filterMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, members]);

  const loadMembers = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`/api/groups/${groupId}/contacts`);
      setMembers(response.data.contacts || []);
    } catch (err: any) {
      console.error('Error loading group members:', err);
      setError(err.response?.data?.error || 'Failed to load group members');
    } finally {
      setLoading(false);
    }
  };

  const loadAllContacts = async () => {
    try {
      const response = await axios.get('/api/contacts');
      const contacts = response.data.contacts || [];
      
      // Filter out contacts that are already members
      const memberIds = new Set(members.map(m => m.id));
      const availableContacts = contacts.filter((c: Contact) => !memberIds.has(c.id));
      
      setAllContacts(availableContacts);
    } catch (err: any) {
      console.error('Error loading contacts:', err);
      setError(err.response?.data?.error || 'Failed to load contacts');
    }
  };

  const filterMembers = () => {
    if (!searchQuery.trim()) {
      setFilteredMembers(members);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = members.filter(member =>
      member.name.toLowerCase().includes(query) ||
      member.phoneNumber.toLowerCase().includes(query) ||
      (member.email && member.email.toLowerCase().includes(query)) ||
      (member.note && member.note.toLowerCase().includes(query))
    );
    setFilteredMembers(filtered);
  };

  const handleOpenAddDialog = async () => {
    await loadAllContacts();
    setSelectedContacts([]);
    setContactSearchQuery('');
    setAddDialogOpen(true);
  };

  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
    setSelectedContacts([]);
    setContactSearchQuery('');
  };

  const handleToggleContact = (contactId: string) => {
    setSelectedContacts(prev =>
      prev.includes(contactId)
        ? prev.filter(id => id !== contactId)
        : [...prev, contactId]
    );
  };

  const handleAddMembers = async () => {
    if (selectedContacts.length === 0) {
      return;
    }

    try {
      setSaving(true);
      setError('');
      await axios.post(`/api/groups/${groupId}/contacts`, {
        contactIds: selectedContacts
      });
      setSuccess(`Added ${selectedContacts.length} member${selectedContacts.length !== 1 ? 's' : ''} successfully`);
      handleCloseAddDialog();
      loadMembers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      console.error('Error adding members:', err);
      setError(err.response?.data?.error || 'Failed to add members');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveMember = async (contact: Contact) => {
    if (!window.confirm(`Remove ${contact.name} from this group?`)) {
      return;
    }

    try {
      setError('');
      await axios.delete(`/api/groups/${groupId}/contacts/${contact.id}`);
      setSuccess('Member removed successfully');
      loadMembers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      console.error('Error removing member:', err);
      setError(err.response?.data?.error || 'Failed to remove member');
    }
  };

  const getFilteredAvailableContacts = () => {
    if (!contactSearchQuery.trim()) {
      return allContacts;
    }

    const query = contactSearchQuery.toLowerCase();
    return allContacts.filter(contact =>
      contact.name.toLowerCase().includes(query) ||
      contact.phoneNumber.toLowerCase().includes(query)
    );
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={onBack} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>
          {groupName}
        </Typography>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={handleOpenAddDialog}
        >
          Add Members
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <TextField
        fullWidth
        size="small"
        placeholder="Search members..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Phone Number</TableCell>
              <TableCell>Email</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredMembers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    {searchQuery ? 'No members found' : 'No members yet. Click "Add Members" to get started.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredMembers.map((member) => (
                <TableRow key={member.id}>
                  <TableCell>{member.name}</TableCell>
                  <TableCell>{member.phoneNumber}</TableCell>
                  <TableCell>{member.email || '-'}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveMember(member)}
                      title="Remove from group"
                      color="error"
                    >
                      <PersonRemoveIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Members Dialog */}
      <Dialog open={addDialogOpen} onClose={handleCloseAddDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add Members to {groupName}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            size="small"
            placeholder="Search contacts..."
            value={contactSearchQuery}
            onChange={(e) => setContactSearchQuery(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          {allContacts.length === 0 ? (
            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
              All contacts are already members of this group
            </Typography>
          ) : (
            <List sx={{ maxHeight: 400, overflow: 'auto' }}>
              {getFilteredAvailableContacts().map((contact) => (
                <ListItem key={contact.id} disablePadding>
                  <ListItemButton onClick={() => handleToggleContact(contact.id)}>
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={selectedContacts.includes(contact.id)}
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={contact.name}
                      secondary={contact.phoneNumber}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog}>Cancel</Button>
          <Button
            onClick={handleAddMembers}
            variant="contained"
            disabled={saving || selectedContacts.length === 0}
          >
            {saving ? <CircularProgress size={24} /> : `Add ${selectedContacts.length} Member${selectedContacts.length !== 1 ? 's' : ''}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default GroupDetail;
